import React, { useEffect, useState, useCallback, useMemo } from 'react';
import ReactFlow, {
  Controls,
  Background,
  useNodesState
  } from 'reactflow';
import 'reactflow/dist/style.css';
import './OrgChart.css';
import AddEntityModal from '../components/AddEntityModal.jsx';

import CustomEdge from '../components/CustomEdge.jsx';

/** Format an ISO date YYYY-MM-DD → DD-Month-YYYY (parsed as local date) */
const formatDisplayDate = dateString => {
    const parts = dateString.split('-');
    if (parts.length !== 3) return dateString;
    const [year, month, day] = parts;
    const monthNames = [
      'January','February','March','April','May','June',
      'July','August','September','October','November','December'
    ];
    const m = parseInt(month, 10) - 1;
    const monthName = monthNames[m] || month;
    const dayNum = parseInt(day, 10);
    const dayStr = String(dayNum).padStart(2, '0');
    return `${dayStr}-${monthName}-${year}`;
};

const edgeTypes = {
  custom: CustomEdge,
};


export default function OrgChart() {

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [masterEdges, setMasterEdges] = useState([]);
  
  // hold the React Flow instance so we can call .setCenter()
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editEnt, setEditEnt] = useState(null);
  const [showAssets, setShowAssets] = useState(false);
  const [showDebts, setShowDebts] = useState(false);
  // IDs for individuals who use “Date of Birth”

  
  // Persist node position when dragging stops
  const onNodeDragStop = useCallback((_, node) => {
    setNodes(nds => nds.map(n => n.id === node.id ? { ...n, position: node.position } : n));
    fetch(`${process.env.REACT_APP_BACKEND_URL_TOOLS}/org-entities/${node.id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ position_x: node.position.x, position_y: node.position.y })
    }).catch(err => console.error('Position save failed:', err));
  }, [setNodes]);

  const onConnect = useCallback(
    (connection) => {
      const { source, target } = connection;
      if (!source || !target) return;

      const edgeId = `e-${source}-${target}`;
      // Optimistically update the UI
      setMasterEdges((edges) => [
        ...edges,
        {
          id: edgeId,
          source,
          target,
          label: '', // Or some default label
          offset1: { x: 0, y: 0 },
          offset2: { x: 0, y: 0 },
          label_x: 0,
          label_y: 0,
          style: { stroke: '#333', strokeWidth: 2 },
          markerEnd: 'arrowclosed',
          type: 'custom'
        }
      ]);

      // Persist edge to backend
      fetch(`${process.env.REACT_APP_BACKEND_URL_TOOLS}/edges/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: edgeId,
          source,
          target,
          label: ''
        })
      }).then(res => {
        if (!res.ok) throw new Error('Edge creation failed');
        return res.json();
      }).then(newEdgeObj => {
        // Replace with server response (to get offsets, label etc.)
        setMasterEdges((edges) => {
          const filtered = edges.filter(e => e.id !== newEdgeObj.id);
          return [
            ...filtered,
            {
              id: newEdgeObj.id,
              source: String(newEdgeObj.source),
              target: String(newEdgeObj.target),
              label: newEdgeObj.label,
              offset1: { x: newEdgeObj.offset1_x, y: newEdgeObj.offset1_y },
              offset2: { x: newEdgeObj.offset2_x, y: newEdgeObj.offset2_y },
              style: { stroke: '#333', strokeWidth: 2 },
              markerEnd: 'arrowclosed',
              type: 'custom'
            }
          ];
        });
      }).catch(err => {
        console.error('Failed to create edge:', err);
        // Optionally remove the optimistic edge if creation fails
        setMasterEdges((edges) => edges.filter(e => e.id !== edgeId));
      });
    },
    [setMasterEdges]
  );  


  // Load data on mount
  useEffect(() => {
    async function loadData() {
      try {
        const listRes = await fetch(`${process.env.REACT_APP_BACKEND_URL_TOOLS}/org-entities/`,{
          method:'GET',
          credentials: 'include',
        });
        if (!listRes.ok) throw new Error('List fetch failed');
        const list = await listRes.json();
        const details = await Promise.all(
          list.map(ent =>
            fetch(`${process.env.REACT_APP_BACKEND_URL_TOOLS}/org-entities/${ent.id}`, {
              method:'GET',
              credentials: 'include',
            })
              .then(res => res.ok ? res.json() : null)
              .catch(() => null)
          )
        );

        const valid = details.filter(d => d && d.entity && d.entity.trim());

        const personIds = new Set(['001', '002']);  // IDs for Maurice Byrne & Siobhan Dempsey
        const newNodes = valid.map((det, i) => {
          const isPersonOrTrust = personIds.has(String(det.id));
          let baseColor;
          if (isPersonOrTrust) {
            baseColor = '#4B0082';
          } else {
            switch ((det.domiciled || '').toLowerCase()) {
              case 'canada': case 'co canada':          baseColor = '#FF0000'; break;
              case 'usa': case 'united states': case 'united states of america': baseColor = '#0000FF'; break;
              case 'ireland':                           baseColor = '#008000'; break;
              default:                                  baseColor = '#FFD580';
            }
          }

          const bgGradient = `linear-gradient(135deg, ${baseColor}33 0%, #ffffff 100%)`;
          const label = det.trading_as || det.entity;

          const posX = typeof det.position_x === 'number' ? det.position_x : (i % 5) * 200; // makes a grid of nodes if can't find position
          const posY = typeof det.position_y === 'number' ? det.position_y : Math.floor(i / 5) * 100;

          const id = String(det.id);
          return {
           id,                                          // string ID
            data: { label },
            position: { x: posX, y: posY },
            draggable: true,
            className: 'org-node',
            style: {
              background: bgGradient,
              border: `3px solid ${baseColor}`,
              borderRadius: '6px',
              padding: '12px',
              boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
              color: '#fff',
              fontWeight: 700,
              transition: 'transform 0.2s ease, box-shadow 0.2s ease'
            }
          };
        });

        const newEdges = valid.flatMap(det => {
          const target = String(det.id);
          const rels = [];
          if (det.owner_a) {
            const src = String(det.owner_a);
            rels.push({ id: `${src}-${target}`, source: src, target });
          }
          if (det.owner_b) {
            const src = String(det.owner_b);
            rels.push({ id: `${src}-${target}`, source: src, target });
          }
          return rels;
        });
        setMasterEdges(newEdges);
        setNodes(newNodes);
                
      } catch (err) {
        console.error('Error loading org data:', err);
      }
    }
    loadData();
  }, [setNodes]);


  // Whenever the user selects a node, reload all edge offsets from the API
  useEffect(() => {
    if (!selected) return;

    async function loadEdgesForSelected() {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL_TOOLS}/edges/`,
          {
            method:'GET',
            credentials: 'include',
          }
        );
        if (!res.ok) throw new Error('Edges fetch failed');
        const edgesList = await res.json();
        // Build a plain array (no ReactFlow‐specific bits here)
        const loaded = edgesList.map(e => ({
          id:        e.id,
          source:    String(e.source),
          target:    String(e.target),
          label:     e.label,
          offset1:   { x: e.offset1_x, y: e.offset1_y },
          offset2:   { x: e.offset2_x, y: e.offset2_y },
          label_x:   e.label_x ?? 0,
          label_y:   e.label_y ?? 0,
          style:     { stroke: '#fff', strokeWidth: 3, fill: 'none', opacity: 1 },
          markerEnd: 'arrowclosed'
        }));
        setMasterEdges(loaded);
      } catch (err) {
        console.error('Failed to reload edges for selected:', err);
      }
    }

    loadEdgesForSelected();
  }, [selected]);

  // Toggle selected on click: deselect if clicking the same node
  const onNodeClick = useCallback((_, node) => {
    if (selected && selected.id === node.id) {
      setSelected(null);
    } else {
      fetch(`${process.env.REACT_APP_BACKEND_URL_TOOLS}/org-entities/${node.id}`,{
        method:'GET',
        credentials: 'include',
      })
        .then(res => res.json())
        .then(data => setSelected(data))
        .catch(err => console.error('Detail fetch error:', err));
    }
  }, [selected]);

  // Highlight logic: dim unrelated nodes and edges
  const highlightNodeIds = useMemo(() => {
    if (!selected) {
      return new Set(nodes.map(n => n.id));
    }

    const selectedId = selected.id;

    // Parent IDs from API or fallback
    const parentIds = Array.isArray(selected.parent_ids)
      ? selected.parent_ids
      : masterEdges.filter(e => e.target === selectedId).map(e => e.source);

    // Child IDs always from edges
    const childIds = masterEdges.filter(e => e.source === selectedId).map(e => e.target);

    // Owner IDs
    const ownerIds = [selected.owner_a, selected.owner_b].filter(Boolean);

    return new Set([selectedId, ...parentIds, ...childIds, ...ownerIds]);
  }, [selected, nodes, masterEdges]);

  const styledNodes = useMemo(
    () => {
      if (!selected) return nodes;
      return nodes.map(n => {
        if (highlightNodeIds.has(n.id)) return n;
        return { ...n, style: { ...n.style, opacity: 0.2 } };
      });
    },
    [nodes, highlightNodeIds, selected]
  );
  

  // Z‐shape edges with two draggable elbows
  // Define edgeTypes so ReactFlow knows about our CustomEdge component

  

  const ownershipElements = useMemo(() => {
    if (!selected || !Array.isArray(selected.shareholders)) return null;
    return selected.shareholders.map((sh, idx) => {
      const ownerId = String(sh.id);
      const ownerNode = nodes.find(n => n.id === ownerId);
      const ownerName = ownerNode ? ownerNode.data.label : ownerId;
      const cls = sh.share_class || '';
      const pct = sh.percentage != null ? sh.percentage + '%' : '';
      return (
        <div key={idx} style={{ marginLeft: '1rem', whiteSpace: 'nowrap' }}>
          <span>{ownerName}</span>
          {(cls || pct) && (
            <span style={{ color: '#F7EAB7', marginLeft: '0.5rem' }}>
              {cls} {pct}
            </span>
          )}
        </div>
      );
    });
  }, [selected, nodes]);

  const formatCurrency = val =>
    val.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 });

  const debts = useMemo(() => {
    if (!selected || !Array.isArray(selected.debts)) return [];
    return selected.debts.map(d => ({
      amount:      d.amount,
      institution: d.institution,
      rate:        d.rate,
      term:        d.term,
      maturity:    d.maturity
    }));
  }, [selected]);

  const totalDebt = debts.reduce((sum, d) => sum + (d.amount || 0), 0);

  // compute the total for assets so we can show it in the Assets header
  const totalAssets = useMemo(
    () =>
      Array.isArray(selected?.assets)
        ? selected.assets.reduce((sum, a) => sum + (a.value || 0), 0)
        : 0,
    [selected]
  );

  const selId = selected ? String(selected.id) : null;

  return (
    <div className="org-chart-container">
      <button
        className="add-entity-btn"
        onClick={() => setShowAdd(true)}
      >
        + Add Entity
      </button>
      <AddEntityModal
        nodes={nodes}
        isOpen={showAdd}
        initialData={editEnt}
        onClose={() => {
          setShowAdd(false);
          setEditEnt(null);
        }}
        onCreated={ent => {
          // 1️⃣ Update nodes
          setNodes(ns => {
            const posX = typeof ent.position_x === 'number' ? ent.position_x : 20;
            const posY = typeof ent.position_y === 'number' ? ent.position_y : 48;
            // match the same color & label logic from your loadData mapping
            const isPersonOrTrust = ['Maurice Byrne','Siobhan Dempsey','Family Trust']
              .includes(ent.entity);
            let baseColor;
            if (isPersonOrTrust) {
              baseColor = '#4B0082';
            } else {
              switch ((ent.domiciled||'').toLowerCase()) {
                case 'canada': baseColor = '#FF0000'; break;
                case 'usa':    baseColor = '#0000FF'; break;
                case 'ireland': baseColor = '#008000'; break;
                default:       baseColor = '#FFD580';
              }
            }
            const bgGradient = `linear-gradient(135deg, ${baseColor}33 0%, #ffffff 100%)`;
            const label = isPersonOrTrust ? ent.entity : ent.trading_as || ent.entity;

            return [
              ...ns.filter(n => String(n.id) !== String(ent.id)),
              {
                id: ent.id,
                data: { label },
                position: { x: posX, y: posY },
                draggable: true,
                className: 'org-node',
                style: {
                  background: bgGradient,
                  border: `3px solid ${baseColor}`,
                  borderRadius: '6px',
                  padding: '12px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                  color: '#fff',
                  fontWeight: 700,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                }
              }
            ];
          });

          // 2️⃣ Rebuild edges from authoritative ent.shareholders
          setMasterEdges(es => {
            // Keep edges not involving this entity
            const keepOther = es.filter(
              e => e.source !== String(ent.id) && e.target !== String(ent.id)
            );

            // Build edges for this entity
            const rebuildThis = (ent.shareholders || []).map(sh => {
              const edgeId = `e-${sh.id}-${ent.id}`;
              const existing = es.find(e => e.id === edgeId); // preserve offsets if available
              return {
                id: edgeId,
                source: String(sh.id),
                target: String(ent.id),
                label: `${sh.share_class || ''} ${sh.percentage != null ? sh.percentage + '%' : ''}`,
                offset1: existing?.offset1 ?? { x: 0, y: 0 },
                offset2: existing?.offset2 ?? { x: 0, y: 0 },
                label_x: existing?.label_x ?? 0,
                label_y: existing?.label_y ?? 0,
                style: { stroke: '#333', strokeWidth: 2 },
                markerEnd: 'arrowclosed'
              };
            });

            return [...keepOther, ...rebuildThis];
          });


          // 4️⃣ Update selected entity
          setSelected(ent);
          setEditEnt(null);
        }}

      />
    <ReactFlow
     // capture the instance on init
      nodes={styledNodes}
      // show only edges connected to the selected node
      edges={
        selected
          ? masterEdges
              .filter(e =>
                e.target === selId || e.source === selId
              )
              .map(e => ({
                id:       e.id,
                type:     'custom',
                source:   e.source,
                target:   e.target,
                data: {
                  label:     e.label,
                  offset1:   e.offset1,
                  offset2:   e.offset2,
                  labelPos: {
                    x: e.label_x ?? 0,
                    y: e.label_y ?? 0
                  },
                  setMasterEdges
                },
                style:     { stroke: '#fff', strokeWidth: 3, fill: 'none', opacity: 1 },
                markerEnd: e.markerEnd
              }))
          : []
      }
      edgeTypes={edgeTypes}
      onNodesChange={onNodesChange}
      onNodeClick={onNodeClick}
      onNodeDragStop={onNodeDragStop}
      onPaneClick={() => setSelected(null)}
      onConnect={onConnect} 
      proOptions={{ hideAttribution: true }}
    >
      <Controls />
      <Background />
    </ReactFlow>

      {selected && (
        <aside className="detail-panel">
          {/* subtle ID up top */}
          <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '0.5rem' }}>
            ID: {selected.id}
          </div>
          <h3>{selected.trading_as || selected.entity}</h3>

          <div className="detail-item" style={{ marginBottom: '1rem' }}>
            <strong>Legal Name:</strong>
            <div style={{ marginLeft: '1rem' }}>{selected.entity}</div>
          </div>

          <div className="detail-item" style={{ marginBottom: '1rem' }}>
            <strong>Activity:</strong>
            <div style={{ marginLeft: '1rem' }}>{selected.activity}</div>
          </div>

          <div className="detail-item" style={{ marginBottom: '1rem' }}>
            <strong>Home Country:</strong>
            <div style={{ marginLeft: '1rem' }}>{selected.domiciled}</div>
          </div>

          {selected.incorporated && (
            <div className="detail-item" style={{ marginBottom: '1rem' }}>
              <strong>Incorporated:</strong>
              <div style={{ marginLeft: '1rem' }}>{selected.incorporated}</div>
            </div>
          )}
          {selected.date_of_incorporation && (
            <div className="detail-item" style={{ marginBottom: '1rem' }}>
             <strong>
              {['Maurice James Byrne','Siobhan Mary Dempsey'].includes(selected.entity)
                ? 'Date of Birth:'
                : 'Date of Incorporation:'}
              </strong>
              <div style={{ marginLeft: '1rem' }}>
                {formatDisplayDate(selected.date_of_incorporation)}
              </div>
            </div>
          )}
          {selected.shareholders?.length > 0 && (
            <div className="detail-item" style={{ marginBottom: '1rem' }}>
              <strong>Ownership:</strong>
              {ownershipElements}
            </div>
          )}

          {Array.isArray(selected.assets) && selected.assets.length > 0 && (
            <div className="detail-item" style={{ marginBottom: '1rem' }}>
              {/* Accordion header */}
              <div
                onClick={() => setShowAssets(v => !v)}
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              >
                <span style={{ marginRight: '0.5rem' }}>
                  {showAssets ? '▼' : '▶'}
                </span>
                <strong>Assets:</strong>{' '}
                <span style={{ marginLeft: '0.5rem', color: '#F7EAB7' }}>
                  {formatCurrency(totalAssets)}
                </span>
              </div>

              {/* Details when expanded */}
              {showAssets &&
                selected.assets.map((a, i) => (
                  <div
                    key={i}
                    style={{ marginTop: i === 0 ? '0.5rem' : '1rem' }}
                  >
                    <div style={{ marginLeft: '1rem' }}>
                      <span style={{ fontWeight: 'normal', color: 'lightblue' }}>Value:</span>{' '}
                      <span style={{ color: '#F7EAB7' }}>
                        {formatCurrency(a.value)}
                      </span>
                    </div>
                    {a.description && (
                      <div style={{ marginLeft: '1rem' }}>
                        <span style={{ fontWeight: 'normal', color: 'lightblue' }}>Description:</span>{' '}
                        <span style={{ color: '#F7EAB7' }}>
                          {a.description}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          )}

          {debts.length > 0 && (
            <div className="detail-item" style={{ marginBottom: '1rem' }}>
              {/* Accordion header */}
              <div
                onClick={() => setShowDebts(v => !v)}
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              >
                <span style={{ marginRight: '0.5rem' }}>
                  {showDebts ? '▼' : '▶'}
                </span>
                <strong>Debt:</strong>{' '}
                <span style={{ color: '#F7EAB7' }}>
                  {formatCurrency(totalDebt)}
                </span>
              </div>

              {/* Details when expanded */}
              {showDebts && debts.map((d, idx) => (
                <div
                  key={idx}
                  style={{ marginTop: idx === 0 ? '0.5rem' : '1rem' }}
                >
                  <div style={{ marginLeft: '1rem' }}>
                    <span style={{ fontWeight: 'normal', color: 'lightblue' }}>Amount:</span>{' '}
                    <span style={{ color: '#F7EAB7' }}>
                      {formatCurrency(d.amount)}
                    </span>
                  </div>

                  <div style={{ marginLeft: '1rem' }}>
                    <span style={{ fontWeight: 'normal', color: 'lightblue' }}>Institution:</span>{' '}
                    <span style={{ color: '#F7EAB7' }}>
                      {d.institution}
                    </span>
                  </div>

                  <div style={{ marginLeft: '1rem' }}>
                    <span style={{ fontWeight: 'normal', color: 'lightblue' }}>Rate:</span>{' '}
                    <span style={{ color: '#F7EAB7' }}>
                      {d.rate}%
                    </span>
                  </div>

                  <div style={{ marginLeft: '1rem' }}>
                    <span style={{ fontWeight: 'normal', color: 'lightblue' }}>Term:</span>{' '}
                    <span style={{ color: '#F7EAB7' }}>
                      {d.term}
                    </span>
                  </div>

                  <div style={{ marginLeft: '1rem' }}>
                    <span style={{ fontWeight: 'normal', color: 'lightblue' }}>Maturity:</span>{' '}
                    <span style={{ color: '#F7EAB7' }}>
                      {formatDisplayDate(String(d.maturity).split('T')[0])}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}



          {selected.free_cashflow != null && (
            <div className="detail-item" style={{ marginBottom: '1rem' }}>
              <strong>Free Cashflow:</strong>
              <div style={{ marginLeft: '1rem', color: selected.free_cashflow < 0 ? 'red' : 'inherit' }}>
                {formatCurrency(selected.free_cashflow)}
              </div>
            </div>
          )}

          {selected.fair_market_value != null && (
            <div className="detail-item" style={{ marginBottom: '1rem' }}>
              <strong>Net Equity:</strong>
              <div style={{ marginLeft: '1rem' }}>
                {formatCurrency(selected.fair_market_value)}
              </div>
            </div>
          )}

          {selected.value_description && (
            <div className="detail-item" style={{ marginBottom: '1rem' }}>
              <strong>Notes:</strong>
              <div style={{ marginLeft: '1rem' }}>
                {selected.value_description}
              </div>
            </div>
          )}

            <div className="detail-item" style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem' }}>
              <button
                style={{
                  backgroundColor: '#28a745',
                  color: '#fff',
                  padding: '0.5rem 1rem',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  setEditEnt(selected);
                  setShowAdd(true);
                }}
              >
                Edit Entity
              </button>
              <button
                style={{
                  backgroundColor: '#e3342f',
                  color: '#fff',
                  padding: '0.5rem 1rem',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                onClick={async () => {
                  if (!window.confirm(`Are you sure you want to delete "${selected.entity}" (ID: ${selected.id})?`)) return;
                  await fetch(`${process.env.REACT_APP_BACKEND_URL_TOOLS}/org-entities/${selected.id}`, { method: 'DELETE', credentials: 'include' });
                  setNodes(ns => ns.filter(n => n.id !== selected.id));
                  setMasterEdges(es => es.filter(e => e.source !== selected.id && e.target !== selected.id));
                  setSelected(null);
                }}
              >
                Delete Entity
              </button>
            </div>
          </aside>
      )}
    </div>
  );
}
